/**
 * @author Maxime Mustarda <maxime@inarix.com>
 * @file licenseList.ts
 * @desc Created on Mon Apr 28 2025 12:13:09
 * @copyright All rights reserved @ Inarix
 */

import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  bindLicenseUser,
  bindPack,
  fetchAvailPacks,
  fetchLicenses,
  unlinkLicenseUser,
} from "../../actions/licenses/licenseListAsyncActions";
import { OrgGroupTypeIds } from "../../actions/users/userTypes";
import { RootState } from "../../../configureStore";
import {
  ExtendedLicense,
  LicenseStatus,
  PackInstance,
} from "../../actions/licenses/licenceTypes";
import { remainingCredsPct } from "../../../components/licenses/LicenceCredits";
import { baseCases } from "../../utils/queryWrapper";
import { logOut } from "../layout";
import { InarixGridFilterModel } from "../dataTable/data";
import { applyLocalFilter } from "../../../components/helpers/helpersSearchs";

const THIRTY_DAYS_MS = 60 * 60 * 24 * 30 * 1000;

export type LicenseListState = {
  loading: boolean;
  error: string;
  licenses: ExtendedLicense[];
  availPacks: PackInstance[];
  licenseIdToIdx: Record<string, number>;
  licenseGidToIdx: Record<number, number[]>;
  packIdToIdx: Record<string, number>;
  unassignedOnly: boolean;
  unassigned: number;
  lowOrEmpty: number;
  expiring: number;
  focusedLicense: string;
  licenseId: string;
  //custom filter component
  filterModel: InarixGridFilterModel;
  showFilterForm: boolean;
  preselectedFilterField: string | null;
  //custom filter component
};

export const initialState: LicenseListState = {
  loading: false,
  error: "",
  licenses: [],
  availPacks: [],
  licenseIdToIdx: {},
  licenseGidToIdx: {},
  packIdToIdx: {},
  unassignedOnly: false,
  unassigned: 0,
  lowOrEmpty: 0,
  expiring: 0,
  focusedLicense: "",

  licenseId: "",
  //custom filter component
  filterModel: { items: [] },
  showFilterForm: false,
  preselectedFilterField: null,
  //custom filter component
};

export const selectUnassignedCount = (state: RootState) =>
  state.LicensesReducer.unassigned;
export const selectLowOrEmptyCount = (state: RootState) =>
  state.LicensesReducer.lowOrEmpty;
export const selectExpiringCount = (state: RootState) =>
  state.LicensesReducer.expiring;
export const selectAvailPackCount = (state: RootState) =>
  state.LicensesReducer.availPacks.length;
export const selectLicenseLoading = (state: RootState) =>
  state.LicensesReducer.loading;
export const selectModalLicense = (state: RootState) =>
  state.LicensesReducer.focusedLicense
    ? state.LicensesReducer.licenses[
        state.LicensesReducer.licenseIdToIdx[
          state.LicensesReducer.focusedLicense
        ]
      ]
    : null;

export const filterselectors = (state: RootState) => ({
  filterModel: state.LicensesReducer.filterModel,
  showFilterForm: state.LicensesReducer.showFilterForm,
  preselectedFilterField: state.LicensesReducer.preselectedFilterField,
  licenses: state.LicensesReducer.licenses,
});

export const licenseAssignation = (state: RootState) => ({
  licenseId: state.LicensesReducer.licenseId,
});

export const selectAvailPacks = (state: RootState) =>
  state.LicensesReducer.availPacks;

export const selectLicencesAndOwners = createSelector(
  (state: RootState) => state.UsersReducer.users.data,
  (state: RootState) => state.LicensesReducer.licenses,
  (state: RootState) => state.LocationsReducer.locations,
  (state: RootState) => state.LicensesReducer.filterModel,
  selectAvailPackCount,
  (users, licenses, locations, filterModel, availPacksCount) => {
    const idToGeoName = locations.reduce<Record<number, string>>((dict, l) => {
      dict[l.id] = l.name;
      return dict;
    }, {});
    const groupToUser = users.reduce<
      Record<number, { username: string; geoId: number }>
    >((dict, u) => {
      u.groups?.forEach((g) => {
        if (g.typeId == OrgGroupTypeIds.user) {
          dict[g.id] = {
            username: u.username,
            geoId: u.associatedGeoPoint?.[0]?.id,
          };
        }
      });
      return dict;
    }, {});

    const mappedLicenses = licenses.map((l) => {
      if (!l.ownerGroupId || l.ownerGroup?.typeId != OrgGroupTypeIds.user) {
        return { ...l, hasPacks: !!availPacksCount };
      }

      return {
        ...l,
        siteName: idToGeoName[groupToUser[l.ownerGroupId]?.geoId],
        username: groupToUser[l.ownerGroupId]?.username,
        hasPacks: !!availPacksCount,
      };
    });

    let filteredLicenses = mappedLicenses;

    if (filterModel?.items && filterModel.items.length > 0) {
      filteredLicenses = mappedLicenses.filter((license) => {
        return filterModel.items.every((filterItem) => {
          if (!filterItem.value || filterItem.value === "") {
            return true;
          }

          if (
            filterItem.service === "text" ||
            filterItem.service === "operator"
          ) {
            return applyLocalFilter(filterItem, license);
          }

          if (filterItem.service === "special-licenses") {
            if (
              filterItem.value === "unassigned" &&
              filterItem.operator === "$eq"
            ) {
              return (
                !license.ownerGroupId ||
                license.ownerGroup?.typeId != OrgGroupTypeIds.user
              );
            }

            if (
              filterItem.value === "unassigned" &&
              filterItem.operator === "$ne"
            ) {
              return !(
                !license.ownerGroupId ||
                license.ownerGroup?.typeId != OrgGroupTypeIds.user
              );
            }
            if (
              filterItem.value === "runningLow" &&
              filterItem.operator === "$eq"
            ) {
              const { pct } = remainingCredsPct(license);
              return pct <= 0.2 && pct >= 0;
            }

            if (
              filterItem.value === "runningLow" &&
              filterItem.operator === "$ne"
            ) {
              const { pct } = remainingCredsPct(license);
              return !(pct <= 0.2 && pct >= 0);
            }

            if (
              filterItem.value === "expiringSoon" &&
              filterItem.operator === "$eq"
            ) {
              return (
                new Date(license.expiryDate).getTime() - Date.now() <
                THIRTY_DAYS_MS
              );
            }

            if (
              filterItem.value === "expiringSoon" &&
              filterItem.operator === "$ne"
            ) {
              return !(
                new Date(license.expiryDate).getTime() - Date.now() <
                THIRTY_DAYS_MS
              );
            }
          }
          return true;
        });
      });
    }

    return filteredLicenses;
  },
);

export const LicensesFilters = createSelector(
  (state: RootState) => state.LicensesReducer,
  (LicensesReducer) => ({
    filterModel: LicensesReducer.filterModel,
    showFilterForm: LicensesReducer.showFilterForm,
    preselectedFilterField: LicensesReducer.preselectedFilterField,
  }),
);

const CreateLicenseListSlice = createSlice({
  name: "LicensesReducer",
  initialState,
  reducers: {
    setLicenseId(state, action) {
      return {
        ...state,
        licenseId: action.payload,
      };
    },
    setFilterModel(state, action) {
      return {
        ...state,
        filterModel: action.payload,
      };
    },

    setShowFilterForm(state, action) {
      return {
        ...state,
        showFilterForm: action.payload,
      };
    },

    setPreselectedFilterField(state, action) {
      return {
        ...state,
        preselectedFilterField: action.payload,
      };
    },

    toggleUnassigned(state) {
      state.unassignedOnly = !state.unassignedOnly;
    },
    focusedLicense(state, action: PayloadAction<string>) {
      state.focusedLicense = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(logOut, () => {
      return initialState;
    });
    baseCases(fetchLicenses, builder, (state, action) => {
      state.loading = false;
      if (!action.payload) {
        return;
      }

      const idToIdx: Record<string, number> = {};
      const gIdToIdx: Record<number, number[]> = {};
      let unassigned = 0;
      let lowOrEmpty = 0;
      let expiring = 0;
      action.payload.forEach((li, idx) => {
        idToIdx[li.id] = idx;
        if (li.ownerGroupId) {
          if (gIdToIdx[li.ownerGroupId]) {
            gIdToIdx[li.ownerGroupId].push(idx);
          } else {
            gIdToIdx[li.ownerGroupId] = [idx];
          }
        }

        if (li.statusConstantId < LicenseStatus.expired) {
          if (!li.ownerGroup || li.ownerGroup?.typeId != OrgGroupTypeIds.user) {
            unassigned++;
          }

          if (new Date(li.expiryDate).getTime() - Date.now() < THIRTY_DAYS_MS) {
            expiring++;
          }

          li.availableCredits /= 1000;
          li.packs?.forEach((p) => (p.initialCredits /= 1000));

          const { pct } = remainingCredsPct(li);
          if (pct <= 0.2) {
            lowOrEmpty++;
          }
        }
      });

      state.unassigned = unassigned;
      state.lowOrEmpty = lowOrEmpty;
      state.expiring = expiring;
      state.licenses = action.payload;
      state.licenseIdToIdx = idToIdx;
      state.licenseGidToIdx = gIdToIdx;
    });

    baseCases(fetchAvailPacks, builder, (state, action) => {
      state.loading = false;
      if (!action.payload) {
        return;
      }

      const idToIdx: Record<string, number> = {};
      action.payload.forEach((p, idx) => {
        p.initialCredits /= 1000;
        idToIdx[p.id] = idx;
      });

      state.availPacks = action.payload;
      state.packIdToIdx = idToIdx;
    });

    baseCases(unlinkLicenseUser, builder, (state, action) => {
      state.loading = false;
      if (!action.payload) {
        return;
      }

      const idx = state.licenseIdToIdx[action.payload.id];
      if (state.licenses[idx]) {
        state.licenses[idx].ownerGroupId = null;
        state.licenses[idx].ownerGroup = undefined;
        state.licenses[idx].statusConstantId = action.payload.statusConstantId;
      }
    });

    baseCases(bindLicenseUser, builder, (state, action) => {
      state.loading = false;
      if (!action.payload) {
        return;
      }

      const idx = state.licenseIdToIdx[action.payload.id];
      if (state.licenses[idx]) {
        state.licenses[idx].ownerGroupId = action.payload.ownerGroupId;
        state.licenses[idx].ownerGroup = action.payload.ownerGroup;
      }
    });

    baseCases(bindPack, builder, (state, action) => {
      state.loading = false;
      if (!action.payload) {
        return;
      }

      const pIdx = state.packIdToIdx[action.payload.id];
      if (state.availPacks[pIdx]) {
        state.availPacks.splice(pIdx, 1);
        delete state.packIdToIdx[action.payload.id];
      }

      const lId = action.payload.boundToLicenseInstanceId;
      if (!lId) {
        return;
      }
      const lIdx = state.licenseIdToIdx[lId];
      if (state.licenses[lIdx]) {
        state.licenses[lIdx].packs.push(action.payload);
      }
    });
  },
});

export const {
  setLicenseId,
  toggleUnassigned,
  focusedLicense,
  setFilterModel,
  setShowFilterForm,
  setPreselectedFilterField,
} = CreateLicenseListSlice.actions;
export const LicensesReducer = CreateLicenseListSlice.reducer;
